// Page publique d'activation d'une invitation (/invitation/:token).
// Vérifie le token, puis permet de définir le mot de passe pour activer le compte.
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";

export default function InvitationActivation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [etat, setEtat] = useState("verification"); // verification | valide | invalide
  const [info, setInfo] = useState(null);
  const [erreur, setErreur] = useState("");
  const [mdp, setMdp] = useState("");
  const [confirme, setConfirme] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    api.get(`/invitations/${token}`)
      .then(({ data }) => { setInfo(data); setEtat("valide"); })
      .catch((e) => { setErreur(formatErreur(e.response?.data?.detail) || "Lien invalide."); setEtat("invalide"); });
  }, [token]);

  const activer = async () => {
    if (mdp.length < 8) { toast.error("Le mot de passe doit contenir au moins 8 caractères."); return; }
    if (mdp !== confirme) { toast.error("Les mots de passe ne correspondent pas."); return; }
    setEnvoi(true);
    try {
      await api.post(`/invitations/${token}/activer`, { mot_de_passe: mdp });
      toast.success("Compte activé ! Vous pouvez maintenant vous connecter.");
      navigate("/login");
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Activation impossible.");
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div data-testid="page-invitation" className="min-h-screen flex items-center justify-center bg-[#E8F4F4] px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-[#4A8A8A]/15 p-8">
        <div className="text-center mb-6">
          <span className="text-xl font-black text-[#1D3D3D]">Porinetia Compta <span className="text-[#4A8A8A]">AERH</span></span>
        </div>

        {etat === "verification" && (
          <div className="flex flex-col items-center gap-3 py-8 text-[#1D3D3D]">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Vérification de l'invitation…</p>
          </div>
        )}

        {etat === "invalide" && (
          <div data-testid="invitation-invalide" className="text-center space-y-4">
            <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
            <p className="text-sm text-[#1D3D3D]/80">{erreur}</p>
            <p className="text-xs text-[#1D3D3D]/60">
              Demandez une nouvelle invitation au gérant de votre entreprise.
            </p>
            <Link to="/login" className="text-[#4A8A8A] text-sm font-medium hover:underline">Aller à la connexion</Link>
          </div>
        )}

        {etat === "valide" && info && (
          <div data-testid="invitation-formulaire" className="space-y-4">
            <div className="flex items-center gap-2 text-[#1D3D3D]">
              <ShieldCheck className="h-5 w-5 text-[#4A8A8A]" />
              <p className="text-sm">
                Bonjour <strong>{info.prenom}</strong>, créez votre accès à{" "}
                <strong>{info.raison_sociale}</strong> en tant que <strong>{info.role_libelle}</strong>.
              </p>
            </div>
            <div>
              <Label>Email</Label>
              <Input value={info.email} disabled className="bg-gray-50" />
            </div>
            <div>
              <Label>Mot de passe</Label>
              <Input data-testid="invitation-mdp" type="password" value={mdp}
                onChange={(e) => setMdp(e.target.value)} placeholder="Au moins 8 caractères" />
            </div>
            <div>
              <Label>Confirmer le mot de passe</Label>
              <Input data-testid="invitation-mdp-confirme" type="password" value={confirme}
                onChange={(e) => setConfirme(e.target.value)} />
            </div>
            <Button data-testid="bouton-activer-compte" onClick={activer} disabled={envoi}
              className="w-full bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
              {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : "Activer mon compte"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
